import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { useLogin, useLoginTOTP, useMe } from "@/api/hooks";

type Phase = "password" | "totp";

export function LoginPage() {
  const [phase, setPhase] = useState<Phase>("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();
  const loginTOTP = useLoginTOTP();
  const me = useMe();
  const nav = useNavigate();
  const loc = useLocation();

  if (me.data) {
    return (
      <div className="text-center py-12">
        <p>You are signed in as <span className="font-mono">{me.data.username}</span>.</p>
        <Link to="/" className="text-blue-700 hover:underline">Go home →</Link>
      </div>
    );
  }

  const next = new URLSearchParams(loc.search).get("next") || "/";

  function landingFor(role: string) {
    if (next !== "/") return next;
    return role === "operator" || role === "admin" ? "/dashboard" : "/receipts";
  }

  function apiErrorMessage(e: unknown, fallback: string): string {
    if (e instanceof ApiError) {
      const body = (typeof e.body === "object" && e.body) ? (e.body as { error?: string }) : null;
      return body?.error ?? fallback;
    }
    return fallback;
  }

  async function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await login.mutateAsync({ username, password });
      if ("requires_2fa" in r) {
        setPhase("totp");
        setCode("");
      } else {
        nav(landingFor(r.user.role));
      }
    } catch (e) {
      setError(apiErrorMessage(e, "Invalid username or password."));
    }
  }

  async function onTOTPSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await loginTOTP.mutateAsync({ code });
      nav(landingFor(r.user.role));
    } catch (e) {
      if (e instanceof ApiError && /restart login/i.test(String(e.body))) {
        setPhase("password");
        setCode("");
        setError("Verification timed out. Please re-enter your password.");
        return;
      }
      setError(apiErrorMessage(e, "Invalid verification code."));
    }
  }

  return (
    <div className="mx-auto max-w-sm py-12">
      <div className="flex justify-center mb-6">
        <img
          src="/panel/animus_logo.svg"
          alt="Animus"
          className="w-80 sm:w-96 h-auto"
        />
      </div>
      <h1 className="text-xl font-semibold mb-4 text-center">
        {phase === "password" ? "Sign in" : "Two-factor verification"}
      </h1>

      {phase === "password" && (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 text-center">
            Receipts are viewable anonymously. Sign in to publish rule packs,
            promote canaries, or manage the fleet.
          </p>
          <form onSubmit={onPasswordSubmit} className="space-y-3">
            <label className="block">
              <span className="text-sm text-slate-700 dark:text-slate-300">Username</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                className="mt-1 block w-full rounded border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 border px-3 py-2 font-mono text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm text-slate-700 dark:text-slate-300">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1 block w-full rounded border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 border px-3 py-2 font-mono text-sm"
              />
            </label>
            {error && (
              <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={login.isPending}
              className="w-full rounded bg-slate-900 dark:bg-purple-600 text-white py-2 font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
            >
              {login.isPending ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </>
      )}

      {phase === "totp" && (
        <>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 text-center">
            Enter the 6-digit code from your authenticator app for{" "}
            <span className="font-mono">{username}</span>.
          </p>
          <form onSubmit={onTOTPSubmit} className="space-y-3">
            <label className="block">
              <span className="text-sm text-slate-700 dark:text-slate-300">Verification code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
                required
                className="mt-1 block w-full rounded border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 border px-3 py-2 font-mono text-lg text-center tracking-widest"
              />
            </label>
            {error && (
              <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loginTOTP.isPending || code.length !== 6}
              className="w-full rounded bg-slate-900 dark:bg-purple-600 text-white py-2 font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
            >
              {loginTOTP.isPending ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => { setPhase("password"); setCode(""); setError(null); }}
              className="w-full text-sm text-slate-600 dark:text-slate-400 hover:underline"
            >
              ← Use a different account
            </button>
          </form>
        </>
      )}
    </div>
  );
}
